import type { ModelRef } from 'vue';
import type { PolicyActionCellRuleInformation } from '#layers/access/components/Policy/PolicyActionCell.vue';
import type { FieldRule, PerActionFieldRules, PerActionFieldRulesOrWildcard, PerActionItemRule, PerActionItemRules, PolicyDocumentAction, PolicyFieldRules, PolicyItemRules, PolicyValidationRules, RulesPolicyDocument, ValidationRule } from '#layers/access/types/permissions/policy';
import type { MonospaceCollection } from '#layers/data-model/public';
import { cloneDeep, isEqual } from 'lodash-es';
import { computed } from 'vue';
import { FIELD_WILDCARD } from '#layers/access/constants/permissions';
import { crudActions } from '#layers/access/types/permissions/crud';
import { isFieldRulesAction } from '#layers/access/utils/policies';
import { omit } from '#layers/base/public';
import { getCollectionFields, isEmptyFilter, useWorkspaceSchemaStore } from '#layers/data-model/public';

interface CollectionOptions {
	collectionName: string;
}
interface CollectionActionOptions {
	action: PolicyDocumentAction;
	collectionName: string;
}

interface CollectionActionRulesOptions<T extends 'itemRules' | 'fieldRules' | 'validationRules'> {
	action: PolicyDocumentAction;
	collectionName: string;
	rules: T extends 'itemRules' ? PerActionItemRules : T extends 'fieldRules' ? PerActionFieldRules : ValidationRule[];
}

interface UsePolicyRulesOptions {
	itemRules: ModelRef<PolicyItemRules>;
	fieldRules: ModelRef<PolicyFieldRules>;
	validationRules: ModelRef<PolicyValidationRules>;
}

export function usePolicyRules({ itemRules, fieldRules, validationRules }: UsePolicyRulesOptions) {
	const { getCollection } = useWorkspaceSchemaStore();

	const content = computed<RulesPolicyDocument>(() => ({
		access: 'rules',
		itemRules: itemRules.value,
		fieldRules: fieldRules.value,
		validationRules: validationRules.value,
		entitlements: [],
	}));

	return {
		// General
		content,
		setAllAccess,
		deleteAllAccess,
		getRuleInformation,

		// Item rules
		setItemRules,
		createItemRule,
		deleteItemRule,
		getItemRuleForCollection,

		// Field rules
		setFieldRules,
		deleteFieldRules,
		setFieldRulesWildcard,
		isWildcardFieldRule,
		getFieldRulesForCollection,

		// Validation rules
		setValidationRules,
		deleteValidationRules,
		getValidationRulesForCollection,

	};

	// General -------------------------------------------------------------------------------------

	function setAllAccess(collectionName: string, actions: readonly PolicyDocumentAction[] = crudActions) {
		const itemRulesForCollection = Object.fromEntries(actions.map((action) => [action, {}]));
		const fieldRulesForCollection = Object.fromEntries(
			actions.filter((action) => isFieldRulesAction(action)).map((action) => [action, FIELD_WILDCARD]),
		);
		itemRules.value = { ...itemRules.value, [collectionName]: itemRulesForCollection };
		fieldRules.value = { ...fieldRules.value, [collectionName]: fieldRulesForCollection };
	}

	function deleteAllAccess(collectionName: string) {
		itemRules.value = omit(itemRules.value, [collectionName]);
		fieldRules.value = omit(fieldRules.value, [collectionName]);
	}

	// Item rules ---------------------------------------------------------------------------------

	function setItemRules({ collectionName, action, rules }: CollectionActionRulesOptions<'itemRules'>) {
		const newItemRules = cloneDeep(itemRules.value ?? {});
		newItemRules[collectionName] ??= {};
		newItemRules[collectionName][action] = rules;
		itemRules.value = newItemRules;
	}

	function createItemRule({ collectionName, action }: CollectionActionOptions) {
		const newItemRules = cloneDeep(itemRules.value ?? {});
		newItemRules[collectionName] ??= {};
		newItemRules[collectionName][action] = {};
		itemRules.value = newItemRules;
	}

	function deleteItemRule({ action, collectionName }: CollectionActionOptions) {
		const newItemRules = cloneDeep(itemRules.value ?? {});
		delete newItemRules?.[collectionName]?.[action];
		itemRules.value = getCleanedItemRules(newItemRules);
	}

	function getItemRuleForCollection({ collectionName, action }: CollectionActionOptions) {
		return itemRules.value?.[collectionName]?.[action] || null;
	}

	// Field rules --------------------------------------------------------------------------------

	function setFieldRules({ collectionName, action, rules }: CollectionActionRulesOptions<'fieldRules'>) {
		const newFieldRules = cloneDeep(fieldRules.value ?? {});
		newFieldRules[collectionName] ??= {};
		newFieldRules[collectionName][action] = rules as PerActionFieldRulesOrWildcard;
		fieldRules.value = newFieldRules;
	}

	function setFieldRulesWildcard({ collectionName, action }: CollectionActionOptions) {
		const newFieldRules = cloneDeep(fieldRules.value ?? {});
		newFieldRules[collectionName] ??= {};
		newFieldRules[collectionName][action] = FIELD_WILDCARD;
		fieldRules.value = newFieldRules;
	}

	function deleteFieldRules({ collectionName, action }: CollectionActionOptions) {
		const newFieldRules = cloneDeep(fieldRules.value ?? {});
		delete newFieldRules?.[collectionName]?.[action];
		fieldRules.value = getCleanedFieldRules(newFieldRules);
	}

	function isWildcardFieldRule({ collectionName, action }: CollectionActionOptions) {
		return fieldRules.value?.[collectionName]?.[action] === FIELD_WILDCARD;
	}

	function getFieldRulesForCollection({ collectionName, action }: CollectionActionOptions) {
		return fieldRules.value?.[collectionName]?.[action] ?? {};
	}

	// Validation rules ---------------------------------------------------------------------------

	function setValidationRules({ collectionName, collectionValidationRules } = {} as { collectionName: string; collectionValidationRules: any[] }) {
		const newValidationRules = cloneDeep(validationRules.value ?? {});
		newValidationRules[collectionName] ??= [];
		newValidationRules[collectionName] = collectionValidationRules;
		validationRules.value = newValidationRules;
	}

	function deleteValidationRules({ collectionName }: CollectionOptions) {
		const newValidationRules = cloneDeep(validationRules.value);
		delete newValidationRules?.[collectionName];
		validationRules.value = newValidationRules;
	}

	function getValidationRulesForCollection({ collectionName }: CollectionOptions) {
		return validationRules.value?.[collectionName] ?? [];
	}

	function getRuleInformation(collection: MonospaceCollection, action: PolicyDocumentAction): PolicyActionCellRuleInformation {
		const fieldRules = getFieldRulesForCollection({ collectionName: collection.apiName, action });
		// Cast to PerActionItemRule since we use optional chaining throughout
		const itemRule = getItemRuleForCollection({ collectionName: collection.apiName, action }) as PerActionItemRule | null;
		const fieldIds = Object.keys(fieldRules ?? {});
		const collectionFields = getCollectionFields(getCollection(collection.id)!, { includeRelationFields: false });
		const hasAllFields = Object.keys(fieldRules ?? {}).length === collectionFields.length;
		const isWildcard = isWildcardFieldRule({ collectionName: collection.apiName, action });

		const noMissingFields = isWildcard || hasAllFields;
		const hasItemRule = itemRule && (!itemRule.filter || action === 'delete');

		const fieldFilterCount = Object.keys(fieldRules ?? {}).reduce((acc, fieldId) => {
			const fieldRule = fieldRules?.[fieldId as keyof typeof fieldRules] as FieldRule;
			acc += isEmptyFilter(fieldRule?.filter) || isEqual(fieldRule?.filter ?? {}, itemRule?.filter ?? {}) ? 0 : 1;
			return acc;
		}, 0);

		if (hasItemRule && ((noMissingFields || fieldIds.length === 0) && isEmptyFilter(itemRule?.filter)) && fieldFilterCount === 0) {
			return { type: 'all' };
		}
		else if (!itemRule && fieldIds.length === 0) {
			return { type: 'none' };
		}
		else {
			// TODO: It seems like the engine does not support relation fields in field rules
			let hiddenFieldCount = collectionFields.length - fieldIds.length;
			const itemFilterConditionCount = itemRule?.filter?._and?.length ?? 0;

			if (isWildcardFieldRule({ collectionName: collection.apiName, action }) || action === 'delete') {
				hiddenFieldCount = 0;
			}

			return {
				type: 'custom',
				hiddenFieldCount,
				itemFilterConditionCount,
				fieldFilterCount,
			};
		}
	}
}

function getCleanedItemRules(rules: PolicyItemRules) {
	for (const key in rules) {
		if (Object.keys(rules[key] ?? {}).length === 0) {
			delete rules[key];
		}
	}

	return rules;
}

function getCleanedFieldRules(rules: PolicyFieldRules) {
	for (const key in rules) {
		const collectionRules = rules[key] as Record<string, unknown>;

		for (const action in collectionRules) {
			if (Object.keys(collectionRules[action] ?? {}).length === 0) {
				delete collectionRules[action];
			}
		}

		if (Object.keys(rules[key] ?? {}).length === 0) {
			delete rules[key];
		}
	}

	return rules;
}
